import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  Modal,
  Button,
  IconButton,
  TextField,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { ReactComponent as PenIcon } from "../../../images/penIcon.svg";
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonDownload from "../../custom-components/CommonDownload";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { ReactComponent as ExportButton } from "../../../images/exportButton.svg";
import AgileTestQuadrantsGraph from "../../custom-components/AgileTestQuadrantsGraph";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as DownloadIcon2 } from "../../../images/DownloadIcon2.svg";
import EditableTestExecutionTable from "./EditableTable";

const TestPlanOuputComponent = ({ testPlanResponse }) => {
  const [output, setOutput] = useState(testPlanResponse || {}); // State to store test plan response
  const [testPlanObject, setTestPlanObject] = useState({});
  const [quadrantData, setQuadrantData] = useState(null); // Store formatted quadrant
  const [openDownload, setOpenDownload] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [activeThumb, setActiveThumb] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const quadrantsImageRef = useRef(null);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  // const [editableData, setEditableData] = useState([...data]);

  // Sync testPlanResponse with output
  useEffect(() => {
    console.log("testPlanResponse received:", testPlanResponse);
    if (!testPlanResponse) return;

    // Sync testPlanResponse with output
    setOutput(testPlanResponse);
    setTestPlanObject({
      "Test Plan Identifier": testPlanResponse?.["Test Plan Identifier"] || "",
      Introduction: testPlanResponse?.["Introduction"] || "",
      "Test Scope": testPlanResponse?.["Test Scope"] || "",
      "Test Data and Settings":
        testPlanResponse?.["Test Data and Settings"] || "",
      "Types of Testing": testPlanResponse?.["Types of Testing"] || {},
      Resources: testPlanResponse?.["Resources"] || "",
      "Test Suite": testPlanResponse?.["Test Suite"] || "",
      "Entry and Exit Criteria":
        testPlanResponse?.["Entry and Exit Criteria"] || "",
      "Test Environment": testPlanResponse?.["Test Environment"] || "",
      Tools: testPlanResponse?.["Tools"] || [],
      "Test Execution Schedule":
        testPlanResponse?.["Test Execution Schedule"] || [],
      "Sprint Activities": testPlanResponse?.["Sprint Activities"] || "",
      "Support Team Details": testPlanResponse?.["Support Team Details"] || "",
      "Roles and Responsibilities":
        testPlanResponse?.["Roles and Responsibilities"] || "",
      Miscellaneous: testPlanResponse?.["Miscellaneous"] || "",
      "Additional Features": testPlanResponse?.["Additional Features"] || {},
      "Risk Analysis": testPlanResponse?.["Risk Analysis"] || "",
    });

    if (testPlanResponse?.["Agile Test Quadrants"]) {
      console.log(
        "Agile Test Quadrants found:",
        testPlanResponse["Agile Test Quadrants"]
      );

      const quadrants = testPlanResponse["Agile Test Quadrants"];
      const formattedQuadrants = prepareQuadrantData(quadrants);
      setQuadrantData(formattedQuadrants);
    } else {
      console.warn("Agile Test Quadrants data is missing in API response.");
    }
  }, [testPlanResponse]);

  function flattenArrayTestPlan(
    formatArray,
    indexPrefix = "",
    indentLevel = 0
  ) {
    const indentation = "    ".repeat(indentLevel); // Adds indentation based on depth

    const formattedArray = formatArray.map((arrayElement, index) => {
      const currentIndex = indexPrefix
        ? `${indexPrefix}.${index + 1}` // Sub-items (1.1, 1.2, etc.)
        : `${index + 1}.`; // Main items (1., 2., etc.)

      // Ensure sub-items inherit numbering without duplicating dots
      const subIndexPrefix = indexPrefix
        ? `${indexPrefix}.${index + 1}`
        : `${index + 1}`;

      if (Array.isArray(arrayElement)) {
        return `${indentation}${currentIndex}\n${flattenArrayTestPlan(
          arrayElement,
          subIndexPrefix,
          indentLevel + 1
        )}`;
      } else if (typeof arrayElement === "object" && arrayElement !== null) {
        return `${indentation}${currentIndex}\n${flattenObjectTestPlan(
          arrayElement,
          subIndexPrefix,
          indentLevel + 1
        )}`;
      }
      return `${indentation}${currentIndex} ${arrayElement}`;
    });

    return formattedArray.join("\n");
  }

  function flattenObjectTestPlan(
    formatObject,
    indexPrefix = "",
    indentLevel = 0
  ) {
    const indentation = "    ".repeat(indentLevel); // Adds indentation based on depth

    const formattedObject = Object.entries(formatObject)
      .map(([key, value], index) => {
        const currentIndex = indexPrefix
          ? `${indexPrefix}.${index + 1}` // Sub-items: Maintain hierarchical numbering (1.1, 1.2, ...)
          : `${index + 1}.`; // Main items: Ensure they have a dot (1., 2., ...)

        const subIndexPrefix =
          indentLevel === 0 ? `${index + 1}` : currentIndex; // Avoid extra dots for sub-items

        if (Array.isArray(value)) {
          return `${indentation}${currentIndex} ${key}:\n${flattenArrayTestPlan(
            value,
            subIndexPrefix,
            indentLevel + 1
          )}`;
        } else if (typeof value === "object" && value !== null) {
          return `${indentation}${currentIndex} ${key}:\n${flattenObjectTestPlan(
            value,
            subIndexPrefix,
            indentLevel + 1
          )}`;
        }
        return `${indentation}${currentIndex} ${key}: ${value}`;
      })
      .join("\n");

    return formattedObject;
  }

  // Function to update testPlanObject
  const updateTestPlanObject = (key, value) => {
    setTestPlanObject((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const { "Test Execution Schedule": _, ...testPlanWordObject } =
    testPlanObject;

  const handleOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  //Formating the test plan data to send to the download component
  const formatTestPlan = (testPlanData) => {
    const formattedTestPlan = {};
    Object.entries(testPlanData).map(([key, value]) => {
      if (Array.isArray(value)) {
        return (formattedTestPlan[key] = flattenArrayTestPlan(value));
      }
      if (!!value && typeof value === "object") {
        return (formattedTestPlan[key] = flattenObjectTestPlan(value));
      }
      return (formattedTestPlan[key] = value);
    });
    return formattedTestPlan;
  };

  //Functionality to copy Test Plan Content
  const formatContentForCopy = (testPlanObject, labelMapping) => {
    return Object.entries(testPlanObject)
      .map(([key, value]) => {
        const label = labelMapping[key];
        const formattedValue = formatValue(value);

        // Skip empty or null values
        if (
          !value ||
          (typeof value === "object" && Object.keys(value).length === 0)
        ) {
          return "";
        }

        return `${label}:${formattedValue}\n`;
      })
      .filter(Boolean) // Remove empty strings
      .join("\n");
  };

  // Function to validate and prepare quadrant data
  const prepareQuadrantData = (quadrants) => {
    if (!quadrants) {
      console.error("Agile Test Quadrants data is missing.");
      return {
        Q1: {
          title: "Unit Tests",
          items: [],
          automation: "None",
          color: "#FF9980",
        },
        Q2: {
          title: "Functional Tests",
          items: [],
          automation: "None",
          color: "#FF4D4D",
        },
        Q3: {
          title: "Exploratory Tests",
          items: [],
          automation: "None",
          color: "#FFB380",
        },
        Q4: {
          title: "Performance Tests",
          items: [],
          automation: "None",
          color: "#FFCCCC",
        },
      };
    }

    if (typeof quadrants === "string") {
      try {
        quadrants = JSON.parse(quadrants);
      } catch (error) {
        console.error("Failed to parse Agile Test Quadrants string:", error);
        return null;
      }
    }

    if (!quadrants || typeof quadrants !== "object") {
      console.error("Agile Test Quadrants data is undefined or invalid.");
      return null;
    }

    const quadrantColors = {
      Q1: "#FF9980", // Functional Tests
      Q2: "#FF4D4D", // Performance Tests
      Q3: "#FFB380", // Usability Tests
      Q4: "#FFCCCC", // Integration Tests
    };

    return Object.keys(quadrants).reduce((acc, quadrantKey) => {
      const quadrantData = quadrants[quadrantKey];

      acc[quadrantKey] = {
        title: quadrantData?.title || "Untitled",
        items: quadrantData?.items || [],
        automation: quadrantData?.automation || "None",
        color: quadrantColors[quadrantKey] || "#FFFFFF",
      };

      return acc;
    }, {});
  };

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const formatValue = (value, indent = "") => {
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === "object") {
        // Handle array of objects (like test execution schedule)
        const headers = Object.keys(value[0]).join("\t");
        const rows = value
          .map((row) => Object.values(row).join("\t"))
          .join("\n");
        return `\n${indent}${headers}\n${indent}${rows}`;
      } else {
        // Handle simple arrays
        return "\n" + value.map((item) => `${indent}- ${item}`).join("\n");
      }
    } else if (typeof value === "object" && value !== null) {
      // Handle nested objects
      return (
        "\n" +
        Object.entries(value)
          .map(([key, val]) => {
            const formattedVal = formatValue(val, indent + "  ");
            return `${indent}- ${key}: ${formattedVal.trimStart()}`;
          })
          .join("\n")
      );
    }
    return value.toString();
  };

  const handleCopyClick = () => {
    const contentToCopy = formatContentForCopy(testPlanObject, labelMapping);
    navigator.clipboard
      .writeText(contentToCopy)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleQuadrantsImageDownload = () => {
    const quadrantsImage = quadrantsImageRef.current;

    if (!quadrantsImage) {
      console.error("Quadrants image is missing.");
      return;
    }

    const serializer = new XMLSerializer();
    const svgStringQuadrantsImage =
      serializer.serializeToString(quadrantsImage);

    // Create a Blob from the string
    const blob = new Blob([svgStringQuadrantsImage], {
      type: "image/svg+xml;charset=utf-8",
    });

    // Create a download link and trigger it
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "image.svg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // const handleSave = (index) => {
  //   setEditingRowIndex(null);
  //   onChange && onChange(editableData); // Optional callback to parent
  // };

  // const handleCancel = () => {
  //   setEditableData([...data]); // Reset to original
  //   setEditingRowIndex(null);
  // };


  const EditableField = ({ label, initialValue, onChange }) => {
    const bulletOrNumberPattern = /^\s*(\d+[\.\)]|[-*•ivxlcIVXLC]+[\.\)]?)\s+/;
    const formatText = (data, level = 0) => {
      if (label === "Test Execution Schedule" && Array.isArray(initialValue)) {
        return <EditableTestExecutionTable data={initialValue} onChange={onChange} />;
      }
      // Test Plan Identifier handling
      if (
        label === "Test Plan Identifier" &&
        typeof data === "object" &&
        data !== null
      ) {
        return (
          <Typography
            variant="body2"
            component="div"
            className="text-base leading-relaxed"
            sx={{
              whiteSpace: "pre-line",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              fontFeatureSettings: '"kern" 1',
              textRendering: "optimizeLegibility",
            }}
          >
            {Object.entries(data)
              .map(([key, val]) => `● ${key}: ${val}`)
              .join("\n")}
          </Typography>
        );
      }

      //Types of Testing handling
      if (
        label === "Types of Testing" &&
        typeof data === "object" &&
        data !== null
      ) {
        return (
          <Typography
            variant="body2"
            component="div"
            className="text-base leading-relaxed"
            sx={{
              whiteSpace: "pre-line",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              fontFeatureSettings: '"kern" 1',
              textRendering: "optimizeLegibility",
            }}
          >
            {Object.entries(data)
              .map(([key, val]) => `● ${key}: ${val}`)
              .join("\n")}
          </Typography>
        );
      }

      //Support Team Details handling
      if (
        label === "Support Team Details" &&
        typeof data === "object" &&
        data !== null
      ) {
        return (
          <Typography
            variant="body2"
            component="div"
            className="text-base leading-relaxed"
            sx={{
              whiteSpace: "pre-line",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              fontFeatureSettings: '"kern" 1',
              textRendering: "optimizeLegibility",
            }}
          >
            {Object.entries(data)
              .map(([key, val]) => `● ${key}: ${val}`)
              .join("\n")}
          </Typography>
        );
      }

      if (Array.isArray(data)) {
        return (
          <Typography
            variant="body2"
            component="div"
            className="text-base leading-relaxed"
            sx={{
              whiteSpace: "pre-line",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              fontFeatureSettings: '"kern" 1',
              textRendering: "optimizeLegibility",
            }}
          >
            {data
              .map((item) => {
                if (typeof item === "object") {
                  return Object.entries(item)
                    .map(
                      ([key, val]) =>
                        `● ${key}:\n  ${formatText(val, level + 1).props.children
                        }`
                    )
                    .join("\n\n");
                }
                return bulletOrNumberPattern.test(item) ? item : `● ${item}`;
              })
              .join("\n")}
          </Typography>
        );
      }

      if (typeof data === "object" && data !== null) {
        return (
          <Typography
            variant="body2"
            component="div"
            className="text-base leading-relaxed"
            sx={{
              whiteSpace: "pre-line",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              fontFeatureSettings: '"kern" 1',
              textRendering: "optimizeLegibility",
            }}
          >
            {Object.entries(data)
              .map(([key, val]) => {
                if (Array.isArray(val)) {
                  return `● ${key}:\n${val
                    .map((item, index) => {
                      if (typeof item === "object" && item !== null) {
                        return `${JSON.stringify(item, null, 2)
                          .replace(/[{}"]/g, "") // Remove curly braces and quotes for clean output
                          .replace(/,/g, "\n      ")}`; // Align indentation
                      }
                      return `   ${index + 1}. ${item}`;
                    })
                    .join("\n")}`;
                }

                if (typeof val === "object" && val !== null) {
                  return `● ${key}:\n${Object.entries(val)
                    .map(
                      ([subKey, subVal], index) =>
                        `   ${index + 1}. ${subKey}:\n       ${typeof subVal === "object"
                          ? JSON.stringify(subVal, null, 2)
                            .replace(/[{}"]/g, "") // Remove curly braces and quotes
                            .replace(/,/g, "\n       ") // Align indentation
                          : subVal
                        }`
                    )
                    .join("\n")}`;
                }

                return `● ${key}:\n   ${val}`;
              })
              .join("\n")}
          </Typography>
        );
      }

      if (typeof data === "string") {
        return (
          <Typography
            variant="body2"
            component="span"
            className="text-base leading-relaxed"
            sx={{
              whiteSpace: "pre-line",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              fontFeatureSettings: '"kern" 1',
              textRendering: "optimizeLegibility",
            }}
          >
            {data.trim()}
          </Typography>
        );
      }

      return null;
    };

    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(
      typeof initialValue === "object"
        ? formatText(initialValue).props.children
        : initialValue
    );
    const [previousValue, setPreviousValue] = useState(value);

    useEffect(() => {
      setValue(formatText(initialValue).props.children);
    }, [initialValue]);

    const handleSave = () => {
      setIsEditing(false);
      onChange(value);
    };

    const handleCancel = () => {
      setIsEditing(false);
      setValue(previousValue);
    };

    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          "&:hover .edit-icon": { visibility: "visible" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="subtitle2"
            component="div"
            color="text.secondary"
            sx={{ fontSize: "14px" }}
          >
            {label}
          </Typography>
          {label !== "Test Execution Schedule" && (
            <IconButton
              size="small"
              onClick={() => setIsEditing(true)}
              sx={{ marginLeft: 1 }}
            >
              <PenIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {isEditing ? (
          <Box sx={{ display: "flex", alignItems: "center", marginTop: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              sx={{ marginRight: 1 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
            <Box>
              <Button
                onClick={handleSave}
                variant="text"
                size="small"
                sx={{ marginRight: 1, color: "#49A3FF", textTransform: "none" }}
              >
                Save
              </Button>
              <Button
                onClick={handleCancel}
                variant="text"
                size="small"
                sx={{ color: "#49A3FF", textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box sx={{ width: "100%" }}>{formatText(initialValue)}</Box>
          </Box>
        )}
      </Box>
    );
  };

  //Test Execution Schedule To render the table in Word Document
  const testExecutionScheduleTableData = (testPlanObject) => {
    const schedule = testPlanObject?.["Test Execution Schedule"];

    if (Array.isArray(schedule) && schedule.length > 0) {
      const headers = Object.keys(schedule[0]);
      const values = schedule.map((eachRow) => Object.values(eachRow));

      return { headers, values };
    }

    return { headers: [], values: [] }; // Return empty table if data is invalid
  };

  //Test Execution Schedule To render the table in PDF
  const testExecutionScheduleTableDataForPDF = (testPlanObject) => {
    const fieldName = "Test Execution Schedule";
    const data = testPlanObject?.[fieldName];

    if (Array.isArray(data) && data.length > 0) {
      const headers = Object.keys(data[0]);
      const values = data.map((row) => Object.values(row));

      return {
        dataType: "table", // ✅ Keep this if needed
        fieldName,
        headers,
        values,
      };
    }

    return null; // Return null if no valid data
  };

  const [imageData, setImageData] = useState(null);

  useEffect(() => {
    const svgElement = quadrantsImageRef.current;

    if (svgElement !== null) {
      // ✅ Prevent infinite loop
      const convertSVGToPNG = (svgElement) => {
        return new Promise((resolve, reject) => {
          if (!(svgElement instanceof SVGElement)) {
            console.error("Provided element is not an SVGElement:", svgElement);
            return reject(new Error("Invalid SVG element")); // ✅ Proper rejection
          }

          try {
            const serializer = new XMLSerializer();
            const svgString = serializer.serializeToString(svgElement);
            const svgBlob = new Blob([svgString], {
              type: "image/svg+xml;charset=utf-8",
            });
            const url = URL.createObjectURL(svgBlob);

            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement("canvas");
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0);
              URL.revokeObjectURL(url);

              const pngDataUrl = canvas.toDataURL("image/png");
              setImageData(pngDataUrl); // ✅ This triggers re-render, but only once
              resolve(pngDataUrl);
            };

            img.onerror = () => reject(new Error("Image loading failed"));
            img.src = url;
          } catch (error) {
            reject(error);
          }
        });
      };

      convertSVGToPNG(svgElement); // ✅ Call the conversion only once
    }
  }, [quadrantsImageRef.current]); // ✅ Trigger only when ref changes

  const labelMapping = {
    "Test Plan Identifier": "Test Plan Identifier",
    Introduction: "Introduction",
    "Test Scope": "Test Scope",
    "Test Data and Settings": "Test Data and Settings",
    "Types of Testing": "Types of Testing",
    Resources: "Resources",
    "Test Suite": "Test Suite",
    "Entry and Exit Criteria": "Entry and Exit Criteria",
    "Test Environment": "Test Environment",
    Tools: "Tools",
    "Test Execution Schedule": "Test Execution Schedule",
    "Sprint Activities": "Sprint Activities",
    "Support Team Details": "Support Team Details",
    "Roles and Responsibilities": "Roles and Responsibilities",
    Miscellaneous: "Miscellaneous",
    "Additional Features": "Additional Features",
    "Risk Analysis": "Risk Analysis",
  };

  const accordionContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        width: "100%",
        padding: "12px",
        boxSizing: "border-box",
        overflowX: "hidden",
        marginTop: -2,
      }}
    >
      {Object.entries(testPlanObject).map(([key, value], index) => (
        <Box key={index} sx={{ marginBottom: 2, width: "100%" }}>
          <EditableField
            label={labelMapping[key]}
            labelVariant="subtitle2"
            initialValue={value}
            variant="body2"
            sx={{ fontSize: "14px", marginLeft: 2 }}
            onChange={(updatedValue) => updateTestPlanObject(key, updatedValue)}
          />
        </Box>
      ))}

      {quadrantData && (
        <div
          style={{
            position: "relative",
            width: "90%",
            padding: "16px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            backgroundColor: "#f9f9f9",
            maxHeight: "650px",
            overflow: "hidden",
            marginTop: 40,
          }}
        >
          <Box
            sx={{
              marginTop: -18,
              marginBottom: -17,
              alignSelf: "flex-start",
              width: "100%",
            }}
          >
            {quadrantData ? (
              <AgileTestQuadrantsGraph
                data={quadrantData}
                quadrantsImageRef={quadrantsImageRef}
              />
            ) : (
              <p>Loading Agile Test Quadrants...</p>
            )}
            {/* <AgileTestQuadrantsGraph
              data={quadrantData}
              quadrantsImageRef={quadrantsImageRef}
            /> */}
          </Box>
          <div
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              display: "flex",
              gap: "8px",
              padding: "8px",
              backgroundColor: "#000000",
              border: "1px solid #ccc",
              borderRadius: "5px",
              alignItems: "center",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <FileDownloadOutlinedIcon
              style={{ cursor: "pointer", color: "#ffffff" }}
              onClick={handleQuadrantsImageDownload}
            />
            <ZoomInIcon
              onClick={handleOpen}
              style={{ cursor: "pointer", color: "#ffffff" }}
            />
          </div>
        </div>
      )}
      <Modal
        open={isModalOpen}
        onClose={handleClose}
        aria-labelledby="zoomed-modal-title"
        aria-describedby="zoomed-modal-description"
        closeAfterTransition
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "50%",
            bgcolor: "background.paper",
            border: "1px solid #000",
            boxShadow: 24,
            p: 4,
            borderRadius: "2px",
            maxHeight: "70vh",
            overflow: "auto",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#000",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              marginTop: -5,
              marginBottom: -5,
              alignSelf: "flex-start",
              width: "100%",
            }}
          >
            <AgileTestQuadrantsGraph data={quadrantData} />
          </Box>
        </Box>
      </Modal>

      <Box sx={{ display: "flex", alignItems: "center", marginTop: 4 }}>
        {/* Thumbs Up Button with Tooltip */}
        <Tooltip title="Like" arrow>
          <Box
            onClick={() => handleThumbClick("like")}
            sx={{
              cursor: "pointer",
              marginRight: 2, // Margin between the buttons
              "&:hover": {
                color: "#49A3FF", // Change color on hover
              },
            }}
          >
            {activeThumb === "like" ? (
              <ThumbUpIcon sx={{ fontSize: "24px" }} /> // Filled thumbs up when clicked
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} /> // Outlined thumbs up by default
            )}
          </Box>
        </Tooltip>

        {/* Thumbs Down Button with Tooltip */}
        <Tooltip title="Dislike" arrow>
          <Box
            onClick={() => handleThumbClick("dislike")}
            sx={{
              cursor: "pointer",
              marginRight: 2, // Margin between the buttons
              "&:hover": {
                color: "#FF4842", // Change color on hover
              },
            }}
          >
            {activeThumb === "dislike" ? (
              <ThumbDownIcon sx={{ fontSize: "24px" }} /> // Filled thumbs down when clicked
            ) : (
              <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} /> // Outlined thumbs down by default
            )}
          </Box>
        </Tooltip>

        {/* Copy Button with Tooltip */}
        <Tooltip title={isCopied ? "Copied!" : "Copy"} arrow>
          <Box
            onClick={handleCopyClick}
            sx={{
              cursor: "pointer",
              "&:hover": {
                color: "#49A3FF", // Change color on hover
              },
            }}
          >
            {isCopied ? (
              <ContentCopyIcon sx={{ fontSize: "24px" }} /> // Filled copy icon when copied
            ) : (
              <ContentCopyOutlinedIcon sx={{ fontSize: "24px" }} /> // Outlined copy icon by default
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  const buttonsgroup = [
    {
      label: "Export",
      startIcon: (
        <ExportButton
          style={{ background: "none", border: "none", opacity: 0.5 }}
        />
      ),
      variant: "text",
      onClick: () => { },
      disabled: true,
    },
    {
      label: "Download",
      variant: "contained",
      startIcon: <DownloadIcon2 />,
      onClick: () => setOpenDownload(true),
      sx: { float: "right" },
    },
  ];

  return (
    <div
      style={{
        backgroundColor: "#F6F3F2",
        padding: "20px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50px",
          width: "110%",
          margin: "0 auto",
          transform: "translateX(-4.5%)",
        }}
      >
        <CommonAccordion
          summary={
            <Typography variant="h6" sx={{ fontSize: "16px" }}>
              Generated Test Plan
            </Typography>
          }
          details={
            <Box
              mb={2}
              sx={{
                height: '500px',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#555",
                },
              }}
            >
              {accordionContent}
            </Box>
          }

          buttons={buttonsgroup}
          type={"output"}
          defaultExpanded={true}
        />
        <CommonDownload
          open={openDownload}
          onClose={() => setOpenDownload(false)}
          wordData={[
            {
              heading: "Test Plan",
              type: "subParagraphs",
              data: formatTestPlan({
                "Test Plan Identifier":
                  testPlanWordObject["Test Plan Identifier"],
                Introduction: testPlanWordObject["Introduction"],
                "Test Scope": testPlanWordObject["Test Scope"],
                "Test Data and Settings":
                  testPlanWordObject["Test Data and Settings"],
                "Types of Testing": testPlanWordObject["Types of Testing"],
                Resources: testPlanWordObject["Resources"],
                "Test Suite": testPlanWordObject["Test Suite"],
                "Entry and Exit Criteria":
                  testPlanWordObject["Entry and Exit Criteria"],
                "Test Environment": testPlanWordObject["Test Environment"],
                Tools: testPlanWordObject["Tools"],
              }),
            },
            {
              heading: "Test Execution Schedule",
              type: "table",
              data: testExecutionScheduleTableData(testPlanObject),
            },
            {
              heading: "",
              type: "subParagraphs",
              data: formatTestPlan({
                "Sprint Activities": testPlanWordObject["Sprint Activities"],
                "Support Team Details":
                  testPlanWordObject["Support Team Details"],
                "Roles and Responsibilities":
                  testPlanWordObject["Roles and Responsibilities"],
                Miscellaneous: testPlanWordObject["Miscellaneous"],
                "Additional Features":
                  testPlanWordObject["Additional Features"],
                "Risk Analysis": testPlanWordObject["Risk Analysis"],
              }),
            },
            {
              heading: "",
              type: "image",
              data: quadrantsImageRef.current,
            },
          ]}
          documents={["docx", "pdf"]}
          serviceName={"Test Plan"}
          serviceResponse={[
            {
              heading: "Test Plan",
              type: "subParagraphs",
              data: formatTestPlan({
                "Test Plan Identifier":
                  testPlanWordObject["Test Plan Identifier"],
                Introduction: testPlanWordObject["Introduction"],
                "Test Scope": testPlanWordObject["Test Scope"],
                "Test Data and Settings":
                  testPlanWordObject["Test Data and Settings"],
                "Types of Testing": testPlanWordObject["Types of Testing"],
                Resources: testPlanWordObject["Resources"],
                "Test Suite": testPlanWordObject["Test Suite"],
                "Entry and Exit Criteria":
                  testPlanWordObject["Entry and Exit Criteria"],
                "Test Environment": testPlanWordObject["Test Environment"],
                Tools: testPlanWordObject["Tools"],
              }),
            },
            {
              heading: "Test Execution Schedule",
              type: "table",
              data: testExecutionScheduleTableDataForPDF(testPlanObject),
            },
            {
              heading: "Test Plan",
              type: "subParagraphs",
              data: formatTestPlan({
                "Sprint Activities": testPlanWordObject["Sprint Activities"],
                "Support Team Details":
                  testPlanWordObject["Support Team Details"],
                "Roles and Responsibilities":
                  testPlanWordObject["Roles and Responsibilities"],
                Miscellaneous: testPlanWordObject["Miscellaneous"],
                "Additional Features":
                  testPlanWordObject["Additional Features"],
                "Risk Analysis": testPlanWordObject["Risk Analysis"],
              }),
            },
            {
              heading: "",
              type: "image",
              data: imageData || "",
            },
          ]}
        />
      </Box>
    </div>
  );
};

export default TestPlanOuputComponent;
